import mergeObjects from '@bybeam/platform-lib/answers/mergeObjects';
import {
  ApplicationConfig,
  ApplicationQuestion,
  ApplicationSection,
  CommonQuestionKeys,
  CompareOperator,
  ComplexField,
  ExpressionType,
  FieldType,
  FormatType,
  IntroPage,
  TextInputType,
} from '@bybeam/platform-types';
import { YesNoOptions } from '@platform-ui/application/utils/index.js';
import cloneDeep from 'lodash/cloneDeep.js';
import { and, answer, eq, format, gt, inList, literal } from '../../ast/utils.js';
import STANDARD_COMPONENTS from '../standards/index.js';
import { LANGUAGES, NOT_SHOW_IN_LIST } from '../standards/options.js';

const HOUSEHOLD_QUESTION = cloneDeep(
  STANDARD_COMPONENTS.definitions.questions[CommonQuestionKeys.HOUSEHOLD_MEMBERS],
);
(HOUSEHOLD_QUESTION.fields[0] as ComplexField).subFields.splice(-1, 1, {
  type: FieldType.Document,
  key: 'incomeDocumentation',
  dynamicLogic: {
    type: ExpressionType.Compare,
    op: CompareOperator.Gt,
    comparators: [answer('monthlyIncome'), literal(0)],
  },
});

const CONFIG: ApplicationConfig = {
  introPages: [
    {
      key: 'what-is-it',
      layout: 'welcome',
      title: 'What is the Economic Mobility Pilot?',
      body: `The Economic Mobility Pilot aims to provide four core elements:
  
1. Direct cash investments to families
2. Freedom of choice for families to use the cash as they deem necessary 
3. Developing community for participants to build and strengthen their social networks
4. Financial coaching services to support long term success`,
    },
    mergeObjects(
      [
        STANDARD_COMPONENTS.definitions.introPages.ApplicationProcess,
        {
          steps: [
            { title: 'The application contains sensitive questions such as your household income' },
            undefined,
            {
              description:
                'If you need to come back to complete your application, you can start where you left off. You can use the progress tracker to jump to a specific section of the application.',
            },
            undefined,
            {
              title: 'Check the website',
              description:
                'If you have questions or need assistance completing the application, please visit the application tab on the [Fairfax County Economic Mobility Website](https://www.fairfaxcounty.gov/neighborhood-community-services/economicmobilitypilot).',
              icon: 'help',
            },
          ],
        } as IntroPage,
      ],
      'merge',
    ),
    {
      key: 'documents',
      layout: 'default',
      title: 'What documents will you need?',
      body: `You can reference the [Fairfax County Economic Mobility Pilot Eligibility Documentation 
List](https://www.fairfaxcounty.gov/neighborhood-community-services/sites/neighborhood-community-services/files/assets/documents/funding%20opportunities/economic%20mobility%20documents%20list.pdf) 
for a full description of what information you will need to complete the application.`,
    },
  ],
  overrides: {
    statuses: [
      {
        status: 'Application',
        label: 'Apply for the pilot',
        message:
          'If you’re randomly selected and eligible, you could receive cash assistance of up to $750 a month for 15 months. Families have the freedom to use the cash as they deem necessary.',
      },
      {
        status: 'AwaitingReview',
        message: 'Thank you for submitting your application! Check back here for updates.',
      },
      {
        status: 'InProgress',
        message: 'You’ve made great progress so far! Keep working on your application.',
      },
      {
        status: 'Denied',
        message:
          'Unfortunately, your application was not selected for participation. If you have questions about the decision, you can contact GIPilot@fairfaxcounty.gov or (703) 324-1050.',
      },
    ],
    submission: {
      title: 'What happens next?',
      bullets: [
        {
          key: 'notification',
          title: 'Notification',
          icon: 'description',
          description:
            'If you are randomly selected as a participant in the Economic Mobility Pilot, you will be contacted via your preferred contact method (phone, email, or text).',
        },
        {
          key: 'enrollment',
          title: 'Enrollment',
          icon: 'mail',
          description:
            'Once notified, randomly selected participants will be provided with information on how to complete the enrollment process',
        },
        {
          key: 'payments',
          title: 'Receiving a payment',
          icon: 'money',
          description:
            'Randomly selected participants will receive the first payment by the end of October 2023.',
        },
      ],
    },
  },
  sections: [
    {
      key: 'applicantInformation',
      name: 'Applicant Information',
      overview: {
        title: 'Applicant Information',
        description:
          'In this section, we will ask some basic questions about you like date of birth, phone number and home address.',
      },
      questionGroups: [
        {
          key: 'applicantInformation',
          name: 'Applicant Information',
          questions: [
            STANDARD_COMPONENTS.definitions.questions[CommonQuestionKeys.DATE_OF_BIRTH],
            {
              key: 'documents.identityVerification',
              copy: {
                title: 'Identity Verification Documentation',
                intro:
                  'Please upload supporting documents related to your identity. This is a required step.',
              },
              layout: 'panel',
              fields: [
                {
                  type: FieldType.Document,
                  key: 'documents.identityVerification',
                  copy: {
                    title: 'Supporting Documents',
                    description: `Documents should be clear and easy to read. You are required to submit at least one of the documents listed below.
        
The following types of documentation can be uploaded for our review process:
- Government issued ID like a driver’s license, passport or ID card from any state, country or territory (may be expired)
- Employer issued photo ID
- Native American Tribal photo ID
- College/University student photo ID`,
                  },
                },
              ],
            },
            mergeObjects(
              [
                STANDARD_COMPONENTS.definitions.questions[CommonQuestionKeys.MAILING_ADDRESS],
                { copy: { title: 'What is your home address?' } } as ApplicationQuestion,
              ],
              'merge',
            ),
            {
              key: 'documents.addressVerification',
              copy: {
                title: 'Address Verification Documentation',
                intro:
                  'Please upload a supporting document confirming that you live at the address you provided in your application. This is a required step.',
              },
              layout: 'panel',
              fields: [
                {
                  type: FieldType.Document,
                  key: 'documents.addressVerification',
                  copy: {
                    title: 'Supporting Documents',
                    description: `Documents should be clear and easy to read. You are required to submit at least one of the documents listed below.
        
The following types of documentation can be uploaded for our review process:
- Government issued ID like a driver’s license, passport or ID card with current address (not expired)
- Current mortgage or lease with your full name and full address (not expired)
- House deed with your name and full address
- Current vehicle registration with your full name and full address (not expired)
- W2 and/or 1099 from tax year 2022
- Voter ID card with your name and address
- Letter from government agency or benefit document with your name and address; must be dated within the last 3 months
- Utility or cell phone bill with your name and address; must be dated within the last 3 months
- First page of a bank statement with your name and address; must be dated within the last 3 months (financial information may be redacted)`,
                  },
                },
              ],
            },
            {
              key: CommonQuestionKeys.PHONE_NUMBER,
              copy: { title: 'What is your phone number?' },
              fields: [
                {
                  key: CommonQuestionKeys.PHONE_NUMBER,
                  type: FieldType.Text,
                  inputType: TextInputType.Phone,
                  copy: 'What is your phone number?',
                },
                {
                  key: 'phone.permissionToCall',
                  copy: 'Do we have permission to call this number?',
                  type: FieldType.RadioList,
                  options: YesNoOptions,
                  dynamicLogic: format(FormatType.Phone, 'phoneNumber'),
                },
                {
                  key: 'phone.permissionForVoicemail',
                  type: FieldType.RadioList,
                  copy: 'Can we leave a voicemail?',
                  options: YesNoOptions,
                  dynamicLogic: and([
                    format(FormatType.Phone, 'phoneNumber'),
                    eq('phone.permissionToCall', 'yes'),
                  ]),
                },
                {
                  key: 'phone.isCellularPhone',
                  type: FieldType.RadioList,
                  copy: 'Is this a cellular phone?',
                  options: YesNoOptions,
                  dynamicLogic: format(FormatType.Phone, 'phoneNumber'),
                },
                {
                  key: 'phone.permissionToText',
                  type: FieldType.RadioList,
                  copy: 'Do we have permission to text you at this number?',
                  options: YesNoOptions,
                  dynamicLogic: and([
                    format(FormatType.Phone, 'phoneNumber'),
                    eq('phone.isCellularPhone', 'yes'),
                  ]),
                },
              ],
            },
            {
              key: 'preferredMethodOfCommunication',
              copy: {
                title: 'What is your preferred method of communication?',
                intro:
                  'We will use your preferred method of contact to notify you if you are randomly selected to participate in the pilot.',
              },
              fields: [
                {
                  type: FieldType.CheckboxGroup,
                  key: 'preferredMethodOfCommunication',
                  options: [
                    { label: 'Email', value: 'email' },
                    { label: 'Text', value: 'text' },
                    { label: 'Phone call', value: 'phoneCall' },
                  ],
                },
                {
                  type: FieldType.Typography,
                  key: 'preferredCommunication.extraCopy',
                  copy: 'Select all languages you are comfortable reading in a text or email',
                  props: { variant: 'largeBody', alignment: 'center' },
                },
                {
                  type: FieldType.Dropdown,
                  key: 'language.language',
                  copy: 'Language',
                  options: LANGUAGES,
                  props: { searchable: true },
                },
              ],
            },
          ],
        },
      ],
    },
    {
      key: 'householdAndEmployment',
      name: 'Household Information & Proof of Income',
      overview: {
        title: 'Household Information & Proof of Total Gross Annual Household Income',
        description:
          'In this section we will gather information about the members of your household and collect documentation pertaining to those that have income.',
      },
      questionGroups: [
        {
          key: 'householdAndEmployment',
          name: 'Household Information & Proof of Income',
          questions: [
            {
              key: 'has16YearOldOrYounger',
              copy: {
                title:
                  'Do you currently have a child that is 16 years old or younger in your household?',
              },
              fields: [
                {
                  type: FieldType.RadioList,
                  key: 'has16YearOldOrYounger',
                  options: YesNoOptions,
                  validation: {
                    required: true,
                    rules: [
                      {
                        rule: eq('has16YearOldOrYounger', 'yes'),
                        message:
                          'You must have a child that is 16 years old or younger to be eligible for this program.',
                      },
                    ],
                  },
                },
              ],
            },
            {
              key: 'documents.childCustodialDocumentation',
              copy: {
                title: 'Child/Custodial Documentation',
                intro:
                  'Please upload supporting documents confirming that you currently have a child under the age of 16 in the home. This is a required step.',
              },
              layout: 'panel',
              fields: [
                {
                  type: FieldType.Document,
                  key: 'documents.childCustodialDocumentation',
                  copy: {
                    title: 'Supporting Documents',
                    description: `Documents should be clear and easy to read. You are required to submit at least one of the documents listed below.

The following types of documentation can be uploaded for our review process:

- Legal document (e.g., current custody agreement, foster care placement agreement) with child(ren)’s name and date of birth
- Government or benefit document that includes the name and date of birth of child(ren)
- School documentation with the name and date of birth of child(ren)
- Birth Certificate`,
                  },
                },
              ],
            },
            mergeObjects(
              [
                HOUSEHOLD_QUESTION,
                {
                  copy: {
                    title: 'Please list yourself and the members of your household',
                    intro: `**You should include yourself and anyone who currently lives in your home.**
    
You must provide one of the documents listed below for each person that contributes financially to the household, including yourself:
- One month of paystubs; must be dated within the last 3 months
- One month of transaction history from gig economy platform(s) such as Uber, Lyft, DoorDash, or Grubhub or self employment ledger; must be dated within the last 3 months
- W2 and/or 1099 from tax year 2022
- If you receive an additional form of income such as child support, please upload a document that verifies the frequency and amount of payments.`,
                  },
                  fields: HOUSEHOLD_QUESTION.fields.map((field) => {
                    if (
                      field.key === 'householdIncome.annualized' &&
                      field.type === FieldType.Calculated
                    )
                      return {
                        ...field,
                        // This validation runs right away instead of onSubmit like other
                        display: {
                          ...field.display,
                          copy: 'Gross Annual Household Income',
                          validation: {
                            rules: [
                              {
                                rule: gt('householdIncome.annualized', 0),
                                message: 'You must have a household income greater than $0',
                              },
                            ],
                          },
                        },
                      };
                    return field;
                  }),
                } as ApplicationQuestion,
              ],
              'merge',
            ),
          ],
        },
      ],
    },
    {
      key: 'preferredPaymentMethod',
      name: 'Preferred Payment Method',
      overview: {
        title: 'Preferred Payment Method',
        description:
          'In this section we will ask for your preferred method of payment if you are randomly selected to participate in the Economic Mobility Pilot.',
      },
      questionGroups: [
        {
          key: 'preferredPaymentMethod',
          name: 'Preferred Payment Method',
          questions: [
            mergeObjects(
              [
                STANDARD_COMPONENTS.definitions.questions.preferredPaymentMethod,
                {
                  copy: {
                    title:
                      'If you’re selected to participate in the Economic Mobility Pilot, how would you like to receive your monthly payments?',
                  },
                } as ApplicationQuestion,
              ],
              'merge',
            ),
            {
              key: 'researchStudy',
              copy: { title: 'Research Study with George Mason University' },
              layout: 'panel',
              fields: [
                {
                  type: FieldType.Typography,
                  key: 'researchStudy',
                  copy: `The Economic Mobility Pilot is working with a research team from 
George Mason University to understand how unconditional cash transfers affect 
the wellbeing of households in our community. The study will involve surveys 
and interviews with individuals and groups. Your eligibility and chances of 
selection will not be affected if you choose not to participate in the study.`,
                  props: { variant: 'largeBody' },
                },
              ],
            },
          ],
        },
      ],
    },
    {
      key: 'demographicInformation',
      name: 'Demographic Information',
      overview: {
        title: 'Demographic Information',
        description:
          'In this section we will ask some questions about your demographics. These responses will not impact your eligibility and you are not required to answer this section. This is for our research purposes only.',
      },
      questionGroups: [
        {
          key: 'demographicInformation',
          name: 'Demographic Information',
          questions: [
            STANDARD_COMPONENTS.definitions.questions[CommonQuestionKeys.RACE],
            STANDARD_COMPONENTS.definitions.questions[CommonQuestionKeys.GENDER],
            {
              key: 'disability',
              copy: {
                title: 'Disability Status',
                intro: 'Do you identify as having a disability? Please select all that apply.',
              },
              skippable: true,
              fields: [
                {
                  type: FieldType.Dropdown,
                  key: 'disability.disabilityStatus',
                  copy: 'Disability Status',
                  props: { multiple: true },
                  validation: { required: false },
                  options: [
                    { label: 'Vision (for example, blindness or partial sight)', value: 'vision' },
                    {
                      label: 'Hearing (for example, deafness or partial hearing)',
                      value: 'hearing',
                    },
                    {
                      label: 'Mobility (for example, walking short distances or climbing stairs)',
                      value: 'mobility',
                    },
                    {
                      label:
                        'Dexterity (for example, lifting and carrying objects, using a keyboard)',
                      value: 'dexterity',
                    },
                    { label: 'Memory', value: 'memory' },
                    { label: 'Mental illness', value: 'mental-illness' },
                    {
                      label: 'Stamina or breathing or fatigue',
                      value: 'stamina-or-breathing-or-fatigue',
                    },
                    {
                      label:
                        'Socially or behaviorally (for example associated with autism spectrum disorder, which includes Asperger’s and attention deficit hyperactivity disorder)',
                      value: 'socially-or-behaviorally',
                    },
                    {
                      label: 'COVID-related disability or lingering side-effects',
                      value: 'covid-related-disability-or-lingering-side-effects',
                    },
                    NOT_SHOW_IN_LIST,
                    { label: 'Prefer not to answer', value: 'prefer-not-to-answer' },
                    { label: 'I do not have a disability', value: 'i-do-not-have-a-disability' },
                  ],
                },
                {
                  type: FieldType.Typography,
                  key: 'disability.other.label',
                  copy: 'Not shown in the list (specify):',
                  props: { variant: 'body', alignment: 'center' },
                  dynamicLogic: inList('disability.disabilityStatus', 'other'),
                },
                {
                  type: FieldType.Text,
                  inputType: TextInputType.Text,
                  key: 'disability.other',
                  copy: 'Disability Status',
                  dynamicLogic: inList('disability.disabilityStatus', 'other'),
                },
              ],
            },
          ],
        },
      ],
    },
    mergeObjects(
      [
        STANDARD_COMPONENTS.definitions.sections.review,
        {
          overview: {
            description: `Please make sure all the information you provided is correct. 
You will not have an opportunity to make any changes to your application once it is submitted.`,
          },
          questionGroups: [
            undefined,
            {
              questions: [
                {
                  copy: {
                    title: 'Application Consent',
                    intro: undefined,
                  },
                  layout: 'panel',
                  fields: [
                    {
                      type: FieldType.Typography,
                      key: 'consent.intro',
                      copy: `Every family’s financial situation is unique. Payments from the Fairfax 
County Economic Mobility Pilot (FCEMP) may affect your ongoing eligibility for public assistance 
benefits, your eligibility in the future for other assistance programs or be considered income for 
tax purposes. All randomly selected applicants will be provided an informational flyer to help them 
understand the potential impact. Participants may opt out (decline) the guaranteed income at any time.

By filling out this application, you give Fairfax County, Beam (technology partner) and United Way 
through the Britepath's Financial Empowerment Center the permission (1) to use the information 
collected to determine your eligibility; (2) to include your household in the selection lottery; and 
(3) if you are randomly selected and deemed eligible, to contact you via your preferred communication 
method. Applying does not commit you to participating in the pilot; if you are selected, you will have 
an opportunity to opt in or decline.

Applicant’s Certification`,
                      props: { variant: 'body' },
                    },
                    {
                      type: FieldType.Checkbox,
                      key: CommonQuestionKeys.AFFIRMATION,
                      copy: `I, {APPLICANT_NAME}, certify that the statements and attachments contained 
in these documents are true, accurate and complete to the best of my knowledge.`,
                    },
                    {
                      type: FieldType.Checkbox,
                      key: 'consent.hasRead',
                      copy: 'I have read the above information.',
                    },
                    {
                      type: FieldType.Checkbox,
                      key: 'consent.isVoluntary',
                      copy: 'I understand that my participation in the FCEMP and the survey is entirely voluntary.',
                    },
                    {
                      type: FieldType.Checkbox,
                      key: 'consent.isRandom',
                      copy: `I understand that even if I am eligible for the program, I will only receive the 
guaranteed income if I am randomly selected.`,
                    },
                    {
                      type: FieldType.Checkbox,
                      key: 'consent.isUnderstand',
                      copy: `I understand that the payments from the FCEMP may impact public assistance benefits 
and/or be considered income for tax purposes.`,
                    },
                    {
                      type: FieldType.Text,
                      key: 'consent.signature',
                      inputType: TextInputType.Signature,
                      copy: 'Electronic Signature',
                    },
                    {
                      type: FieldType.Date,
                      key: 'consent.date',
                      copy: "Today's Date",
                      validation: { required: true, allowedRange: 'today' },
                    },
                    {
                      type: FieldType.Typography,
                      key: 'consent.questions',
                      copy: `If you have questions about the program, please contact FCEMP program staff at 
Fairfax County by emailing GIpilot@fairfaxcounty.gov or calling (703) 324-1050.`,
                      props: { variant: 'body' },
                    },
                  ],
                },
                {
                  key: 'researchConsent',
                  copy: { title: 'Research Consent' },
                  layout: 'panel',
                  fields: [
                    {
                      type: FieldType.Typography,
                      key: 'researchConsent.intro',
                      copy: `In addition to investing in families, Fairfax County is partnering 
with George Mason University to invest in research to understand how supporting families works 
best. This includes analyzing the information on this application.`,
                      props: { variant: 'body' },
                    },
                    {
                      type: FieldType.RadioList,
                      key: 'researchConsent.consent',
                      options: [
                        {
                          label: `By checking this box, you **AGREE** to have your application data 
analyzed in aggregate by Fairfax County’s program partners (Beam and George Mason University). 
This will be done WITHOUT your name, or any identifying information made public or available to 
Fairfax County. Participation in this research does not impact your eligibility for this program.`,
                          value: 'yes',
                        },
                        {
                          label: `By checking this box, you do **NOT AGREE** to have your application 
data analyzed in aggregate by Fairfax County’s program partners (Beam and George Mason University). 
Declining to participate in this research does not impact your eligibility for this program.`,
                          value: 'no',
                        },
                      ],
                    },
                    {
                      type: FieldType.Date,
                      key: 'researchConsent.date',
                      copy: "Today's Date",
                      validation: { required: true, allowedRange: 'today' },
                    },
                  ],
                },
              ],
            },
          ],
        } as ApplicationSection,
      ],
      'merge',
    ),
  ],
};

export default CONFIG;
